
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useDebounce } from '@/hooks/useDebounce';

export const useAdminGames = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const pageSize = 15;
  const queryClient = useQueryClient();

  // Fetch games with pagination and server-side filtering
  const { data, isLoading } = useQuery({
    queryKey: ['admin-games', page, debouncedSearchTerm, categoryFilter, statusFilter],
    queryFn: async () => {
      let query = supabase
        .from('games')
        .select('*', { count: 'exact' })
        // Use consistent ordering with both created_at and id to prevent non-deterministic results
        .order('created_at', { ascending: false })
        .order('id', { ascending: false }); // Secondary sort by id for consistency

      if (debouncedSearchTerm) {
        query = query.ilike('title', `%${debouncedSearchTerm}%`);
      }
      if (categoryFilter !== 'all') {
        query = query.eq('category', categoryFilter);
      }
      if (statusFilter !== 'all') {
        switch (statusFilter) {
          case 'hidden':
            query = query.eq('hidden', true);
            break;
          case 'visible':
            query = query.eq('hidden', false);
            break;
          case 'featured':
            query = query.eq('featured', true);
            break;
          case 'trending':
            query = query.eq('trending', true);
            break;
          case 'new':
            query = query.eq('is_new', true);
            break;
          case 'unconfigured':
            // Games that have fake_play_count 0 OR rating 0 OR NULL values
            query = query.or('fake_play_count.is.null,fake_play_count.eq.0,rating.is.null,rating.eq.0,ratings_count.is.null,ratings_count.eq.0');
            break;
        }
      }

      // Calculate pagination with zero-based offset
      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;
      
      console.log(`Admin Games Pagination: page=${page}, pageSize=${pageSize}, from=${from}, to=${to}`);
      
      // Apply range AFTER all filters and ordering
      query = query.range(from, to);

      const { data, error, count } = await query;
      if (error) {
        console.error('Admin games query error:', error);
        throw error;
      }
      
      console.log(`Admin Games: Retrieved ${data?.length || 0} games for page ${page}, total count: ${count}`);
      console.log('First game title:', data?.[0]?.title, 'Last game title:', data?.[data?.length - 1]?.title);
      console.log('First game ID:', data?.[0]?.id, 'Last game ID:', data?.[data?.length - 1]?.id);
      
      const games = data?.map(game => ({
        ...game,
        real_play_count: game.play_count || 0,
        display_play_count: game.fake_play_count ?? game.play_count ?? 0
      })) || [];

      return { games, count: count ?? 0 };
    },
    staleTime: 60 * 1000,
  });

  // Fetch categories for filter
  const { data: categories = [] } = useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .order('name');
      if (error) throw error;
      return data;
    },
  });

  // Function to select all unconfigured games across all pages
  const selectAllUnconfiguredGames = async () => {
    console.log('Selecting all unconfigured games...');
    
    try {
      let allGameIds: string[] = [];
      let from = 0;
      const batchSize = 1000;
      let hasMore = true;

      while (hasMore) {
        const { data, error } = await supabase
          .from('games')
          .select('id, title, fake_play_count, rating, ratings_count')
          .or('fake_play_count.is.null,fake_play_count.eq.0,rating.is.null,rating.eq.0,ratings_count.is.null,ratings_count.eq.0')
          .range(from, from + batchSize - 1);
        
        if (error) {
          console.error('Error fetching unconfigured games:', error);
          toast.error('Failed to fetch unconfigured games');
          return [];
        }
        
        if (data && data.length > 0) {
          const gameIds = data.map(game => game.id);
          allGameIds = [...allGameIds, ...gameIds];
          console.log(`Batch ${Math.floor(from / batchSize) + 1}: Found ${data.length} unconfigured games`);
          console.log('Sample games from batch:', data.slice(0, 3));
          from += batchSize;
          hasMore = data.length === batchSize;
        } else {
          hasMore = false;
        }
      }
      
      console.log(`Total found ${allGameIds.length} unconfigured games`);
      if (allGameIds.length === 0) {
        toast.info('No unconfigured games found');
      } else {
        toast.success(`Selected ${allGameIds.length} unconfigured games`);
      }
      return allGameIds;
    } catch (error) {
      console.error('Error selecting unconfigured games:', error);
      toast.error('Failed to select unconfigured games');
      return [];
    }
  };

  // Function to select games with missing or zero fake play count
  const selectAllUnconfiguredPlayCount = async () => {
    console.log('Selecting games with missing or zero play count...');
    
    try {
      let allGameIds: string[] = [];
      let from = 0;
      const batchSize = 1000;
      let hasMore = true;

      while (hasMore) {
        const { data, error } = await supabase
          .from('games')
          .select('id, title, fake_play_count')
          .or('fake_play_count.is.null,fake_play_count.eq.0')
          .range(from, from + batchSize - 1);
        
        if (error) {
          console.error('Error fetching games with missing play count:', error);
          toast.error('Failed to fetch games with missing play count');
          return [];
        }
        
        if (data && data.length > 0) {
          const gameIds = data.map(game => game.id);
          allGameIds = [...allGameIds, ...gameIds];
          console.log(`Batch ${Math.floor(from / batchSize) + 1}: Found ${data.length} games with missing/zero play count`);
          console.log('Sample games from batch:', data.slice(0, 3));
          from += batchSize;
          hasMore = data.length === batchSize;
        } else {
          hasMore = false;
        }
      }
      
      console.log(`Total found ${allGameIds.length} games with missing/zero play count`);
      if (allGameIds.length === 0) {
        toast.info('No games with missing/zero play count found');
      } else {
        toast.success(`Selected ${allGameIds.length} games with missing/zero play count`);
      }
      return allGameIds;
    } catch (error) {
      console.error('Error selecting games with missing play count:', error);
      toast.error('Failed to select games with missing play count');
      return [];
    }
  };

  // Function to select games with missing or zero ratings
  const selectAllUnconfiguredRatings = async () => {
    console.log('Selecting games with missing or zero ratings...');
    
    try {
      let allGameIds: string[] = [];
      let from = 0;
      const batchSize = 1000;
      let hasMore = true;

      while (hasMore) {
        const { data, error } = await supabase
          .from('games')
          .select('id, title, rating, ratings_count')
          .or('rating.is.null,rating.eq.0,ratings_count.is.null,ratings_count.eq.0')
          .range(from, from + batchSize - 1);
        
        if (error) {
          console.error('Error fetching games with missing ratings:', error);
          toast.error('Failed to fetch games with missing ratings');
          return [];
        }
        
        if (data && data.length > 0) {
          const gameIds = data.map(game => game.id);
          allGameIds = [...allGameIds, ...gameIds];
          console.log(`Batch ${Math.floor(from / batchSize) + 1}: Found ${data.length} games with missing/zero ratings`);
          console.log('Sample games from batch:', data.slice(0, 3));
          from += batchSize;
          hasMore = data.length === batchSize;
        } else {
          hasMore = false;
        }
      }
      
      console.log(`Total found ${allGameIds.length} games with missing/zero ratings`);
      if (allGameIds.length === 0) {
        toast.info('No games with missing/zero ratings found');
      } else {
        toast.success(`Selected ${allGameIds.length} games with missing/zero ratings`);
      }
      return allGameIds;
    } catch (error) {
      console.error('Error selecting games with missing ratings:', error);
      toast.error('Failed to select games with missing ratings');
      return [];
    }
  };

  return {
    searchTerm,
    setSearchTerm,
    categoryFilter,
    setCategoryFilter,
    statusFilter,
    setStatusFilter,
    page,
    setPage,
    pageSize,
    games: data?.games ?? [],
    totalGames: data?.count ?? 0,
    pageCount: Math.ceil((data?.count ?? 0) / pageSize),
    categories,
    isLoading,
    queryClient,
    selectAllUnconfiguredGames,
    selectAllUnconfiguredPlayCount,
    selectAllUnconfiguredRatings
  };
};
